import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import CommentTextOutlineIcon from 'mdi-react/CommentTextOutlineIcon';
import CommentOutlineIcon from 'mdi-react/CommentOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import Checkbox from '../../../shared/components/custom/Checkbox';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import CommentModal from './CommentModal';
import ShareModal from '../../../shared/components/custom/ShareModal';
import { getBaseDate } from '../../../util/date-and-time';

const WaitlistPage = ({ eventId }) => {
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  // The delete modal holds either one waiter's id or 'selected' (bulk)
  const [handleModal, deleteVisible, deleteTarget] = useModal();
  const isSomeoneSelected = useMemo(
    () => waiters.some((waiter) => waiter.toggled),
    [waiters]
  );

  const p = partial('EventsPage');
  const s = partial('shared');
  const epp = partial('EventParticipantsPage');

  const getWaitingList = async () => {
    setLoading(true);
    try {
      const [resWaiters, clubCompanies] = await Promise.all([
        Api.events.getWaitingParticipants(eventId),
        Api.companies.getClubCompanies(),
      ]);
      setWaiters(
        resWaiters.map((item) => ({
          ...item,
          toggled: false,
          company: clubCompanies.find((comp) => comp.id === item.companyId),
        }))
      );
      setLoading(false);
    } catch {
      setLoading(false);
      toast.error(s('somethingWentWrong'));
    }
  };

  useEffect(() => {
    getWaitingList();
  }, []);

  const targetsFor = (target) =>
    target === 'selected'
      ? waiters.filter((item) => item.toggled)
      : waiters.filter((item) => item.id === target);

  const handleDelete = async (target) => {
    setLoading(true);
    try {
      const partipantsToDelete = targetsFor(target);
      await Promise.all(
        partipantsToDelete.map((participant) =>
          Api.events.deleteWaitingParticipant(participant.id)
        )
      );
    } catch {
      toast.error(epp('participantDeletingERROR'));
    } finally {
      handleModal();
      getWaitingList();
    }
  };
  const handlePromote = async (target) => {
    setLoading(true);
    try {
      const partipantsToPromote = targetsFor(target);
      await Promise.all(
        partipantsToPromote.map((participant) =>
          Api.events.promoteEventParticipant(participant.id)
        )
      );
    } catch {
      toast.error(epp('participantDeletingERROR'));
    } finally {
      getWaitingList();
    }
  };

  const toggleWaiter = (index) => {
    const waitersCopy = [...waiters];
    waitersCopy[index].toggled = !waitersCopy[index].toggled;
    setWaiters(waitersCopy);
  };
  const toggleAll = (_, toggled) =>
    setWaiters(waiters.map((waiter) => ({ ...waiter, toggled })));
  const selectedCount = waiters.filter((waiter) => waiter.toggled).length;
  const allSelected = waiters.length > 0 && selectedCount === waiters.length;

  const renderWaitlist = () => {
    if (!waiters.length) {
      return (
        <tr>
          <td colSpan={5} className="text-muted text-center py-4">
            {p('noWaiters')}
          </td>
        </tr>
      );
    }
    return waiters.map((waiter, i) => {
      // Phone and company sit under the name, like the participant list
      const details = [waiter.phoneNumber, waiter?.company?.name].filter(
        Boolean
      );
      return (
        <tr key={`waiter-${waiter.id}`}>
          <td className="table-col--check">
            <Checkbox
              index={1}
              toggled={waiter.toggled}
              single
              handleToggle={() => toggleWaiter(i)}
            />
          </td>
          <td className="table-stack__primary">
            {`${waiter.firstname} ${waiter.lastname}`}
            {details.length > 0 && (
              <small className="text-muted d-block">
                {details.join(' · ')}
              </small>
            )}
          </td>
          <td data-label={s('date')}>{getBaseDate(waiter.createdAt)}</td>
          <td data-label={s('guests')}>{waiter.friendAmount}</td>
          <td className="table-stack__actions">
            <div className="icon-actions">
              <SquareIconButton
                icon={
                  waiter.additionalNotes ? (
                    <CommentTextOutlineIcon />
                  ) : (
                    <CommentOutlineIcon />
                  )
                }
                active={!!waiter.additionalNotes}
                title={waiter.additionalNotes ? p('editNotes') : p('addNotes')}
                id={`waiter-notes-${waiter.id}`}
                onClick={() => setSelectedWaiter(waiter)}
              />
              <SquareIconButton
                icon={<AccountPlusOutlineIcon />}
                title={p('promote')}
                tone="primary"
                id={`waiter-promote-${waiter.id}`}
                onClick={() => handlePromote(waiter.id)}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                title={s('delete')}
                tone="danger"
                id={`waiter-delete-${waiter.id}`}
                onClick={() => handleModal(waiter.id)}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  const handleCloseCommentModal = () => {
    setSelectedWaiter(null);
  };
  return (
    <>
      <ShareModal
        visible={!!selectedWaiter}
        title={selectedWaiter?.additionalNotes ? p('editNotes') : p('addNotes')}
        size="md"
        handleModal={handleCloseCommentModal}
      >
        <CommentModal
          selectedParticipantForComment={selectedWaiter}
          handleCloseModal={handleCloseCommentModal}
          getPaticipants={getWaitingList}
          isWaitlist
        />
      </ShareModal>
      <DeleteModal
        visible={deleteVisible}
        handleModal={handleModal}
        type={p('waiter')}
        modalAction={() => handleDelete(deleteTarget)}
      />
      <Loading loading={loading} />
      <FormCard
        title={
          <>
            {p('waitlist')}
            {waiters.length > 0 && (
              <span className="form-card__count">{waiters.length}</span>
            )}
          </>
        }
        actions={
          isSomeoneSelected && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span className="text-muted small">
                {p('selectedCount', { count: selectedCount })}
              </span>
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                title={s('delete')}
                tone="danger"
                id="waitlist-delete-selected"
                onClick={() => handleModal('selected')}
              />
              <Button
                size="sm"
                color="primary"
                className="mb-0"
                onClick={() => handlePromote('selected')}
              >
                {p('promote')}
              </Button>
            </div>
          )
        }
        className="m-3"
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th className="table-col--check">
                <Checkbox
                  handleToggle={toggleAll}
                  toggled={allSelected}
                  single
                  index={1}
                />
              </th>
              <th>{s('name')}</th>
              <th>{s('date')}</th>
              <th>{s('guests')}</th>
              <th />
            </tr>
          </thead>
          <tbody>{renderWaitlist()}</tbody>
        </Table>
      </FormCard>
    </>
  );
};

export default WaitlistPage;
